import { SEO } from "@/components/SEO";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "@/lib/router-compat";
import {
  AlertTriangle,
  ArrowRight,
  Bot,
  FileText,
  Globe,
  LineChart,
  Plane,
  Ship,
  Sparkles,
  Truck,
  Wallet,
  Workflow,
} from "lucide-react";

const CANONICAL = "https://cargonova.ai/features";
const CANONICAL_ZH = "https://cargonova.ai/zh/features";
const CANONICAL_TH = "https://cargonova.ai/th/features";

const contentEn = {
  eyebrow: "Platform Features",
  title:
    "AI Freight Management System Features\nInside the CargoNova Platform",
  summary:
    "CargoNova is AI-native freight forwarding software: a cloud freight management system where AI agents handle rates, quoting, booking, documents, customs and settlement across ocean, air and cross-border trucking.",
  cta1: "Book a demo",
  cta2: "What is CargoNova?",

  painTitle: "The problems this replaces",
  pains: [
    "Quotes take hours because rates live in inboxes, spreadsheets and carrier portals.",
    "Documents are re-typed three or four times between HBL, invoice and customs filing.",
    "Legacy TMS licences are expensive, slow to implement and painful to change.",
    "Managers only see margin after the shipment closes — never while it can still be fixed.",
  ],

  featuresTitle: "Six core capabilities",
  features: [
    { icon: LineChart, title: "Rate management", body: "Contract, spot and surcharge rates in one searchable rate engine, refreshed automatically and versioned by lane, carrier and validity." },
    { icon: Sparkles, title: "AI quoting", body: "AI agents read inbound inquiries, match the best routing and return a branded quotation in minutes instead of hours." },
    { icon: Workflow, title: "Booking & tracking", body: "Bookings pushed to carriers and partners, with milestone tracking that keeps shippers and internal teams in sync." },
    { icon: FileText, title: "Document AI", body: "HBL, MBL, invoices and packing lists extracted, validated and cross-checked automatically — no re-keying." },
    { icon: Globe, title: "Customs & compliance", body: "Declarations drafted and pre-checked against destination rules, with multi-country and multi-language support." },
    { icon: Wallet, title: "Settlement & margin", body: "Costs, revenues and cash flow reconciled per shipment, with live margin dashboards and exception alerts." },
  ],

  modesTitle: "Built for three transport modes",
  modes: [
    { icon: Ship, title: "Ocean freight software", body: "FCL and LCL, schedule and space management, D&D exposure and carrier performance in one view." },
    { icon: Plane, title: "Air freight software", body: "Chargeable-weight logic, AWB handling, ULD planning and time-critical exception management." },
    { icon: Truck, title: "Cross-border trucking", body: "China–SEA overland lanes, border-crossing documentation and multi-leg cost allocation." },
  ],

  compareTitle: "Compared with legacy freight software",
  compareRows: [
    { label: "Architecture", legacy: "On-premise or hosted legacy TMS", nova: "AI-native, cloud freight management system" },
    { label: "Pricing", legacy: "Expensive licences and modules", nova: "Usage-based, far lower total cost" },
    { label: "Go-live", legacy: "6–18 months of implementation", nova: "Weeks, with no rip-and-replace" },
    { label: "Automation", legacy: "Rules and RPA scripts", nova: "AI agents that see, calculate and write back" },
  ],
  compareCta: "CargoNova vs CargoWise",

  faqTitle: "Frequently asked questions",
  faqs: [
    { q: "What features should an AI freight management system have?", a: "At minimum: a live rate engine, AI quoting, booking and milestone tracking, document extraction, customs support and automated settlement with margin reporting. CargoNova covers all six in one platform." },
    { q: "Does CargoNova replace my existing systems?", a: "No. CargoNova integrates with modern APIs and legacy carrier portals through a See–Calculate–Write loop, so you can adopt it without a rip-and-replace project." },
    { q: "Which transport modes are supported?", a: "Ocean freight (FCL and LCL), air freight and cross-border trucking, with multi-currency and multi-language operations." },
  ],

  ctaTitle: "See these features on your own shipments",
  ctaBody: "Book a walkthrough and we will map CargoNova's AI agents to your current workflow.",
};

const contentZh: typeof contentEn = {
  eyebrow: "平台功能",
  title: "国际货代 AI 系统功能全览\nCargoNova 平台核心能力",
  summary:
    "CargoNova 是 AI 原生的国际货代系统：在云原生货代管理平台之上，由 AI Agent 完成运价、报价、订舱、单证、关务与结算，覆盖海运、空运与跨境陆运全场景。",
  cta1: "预约演示",
  cta2: "什么是 CargoNova？",

  painTitle: "它解决的核心痛点",
  pains: [
    "运价散落在邮箱、Excel 与船司门户，一份报价往往要等数小时。",
    "同一批数据在提单、发票与报关单之间被重复录入三四遍。",
    "传统货代软件（legacy TMS）license 昂贵、实施周期长、改动困难。",
    "利润只能在票结之后才看到，错过了可以补救的窗口。",
  ],

  featuresTitle: "六大核心功能",
  features: [
    { icon: LineChart, title: "运价管理", body: "合约价、现舱价与附加费统一入库，按航线、船司与有效期自动更新并留存版本，随时可查。" },
    { icon: Sparkles, title: "AI 智能报价", body: "AI Agent 自动解析询盘、匹配最优routing，几分钟内输出标准报价单，替代小时级人工比价。" },
    { icon: Workflow, title: "订舱与轨迹跟踪", body: "自动向船司与合作伙伴下单订舱，关键节点实时回传，客户与内部团队信息同步。" },
    { icon: FileText, title: "单证智能识别", body: "提单、发票、装箱单自动提取、校验并交叉比对，彻底告别重复录入。" },
    { icon: Globe, title: "关务与合规", body: "按目的国规则自动起草并预审申报资料，支持多国家、多语言合规要求。" },
    { icon: Wallet, title: "结算与利润管理", body: "逐票自动对账成本、收入与现金流，利润看板实时预警异常票据。" },
  ],

  modesTitle: "覆盖三种运输方式",
  modes: [
    { icon: Ship, title: "海运货代系统", body: "整柜与拼箱、船期与舱位管理、滞港滞箱风险与船司履约表现一屏掌握。" },
    { icon: Plane, title: "空运货代系统", body: "计费重逻辑、AWB 处理、板箱规划与时效异常处理全流程覆盖。" },
    { icon: Truck, title: "跨境陆运", body: "中国—东南亚陆运通道、口岸单证与多段运输成本分摊。" },
  ],

  compareTitle: "与传统货代软件的差别",
  compareRows: [
    { label: "架构", legacy: "本地部署或托管的传统 TMS", nova: "AI 原生的云端货代管理系统" },
    { label: "价格", legacy: "License 与模块费用高昂", nova: "按用量计费，总体成本更优" },
    { label: "上线周期", legacy: "实施 6–18 个月", nova: "以周为单位，无需替换现有系统" },
    { label: "自动化方式", legacy: "规则引擎与 RPA 脚本", nova: "AI Agent 看-算-写自动闭环" },
  ],
  compareCta: "CargoNova vs CargoWise",

  faqTitle: "常见问题",
  faqs: [
    { q: "国际货代 AI 系统应该具备哪些功能？", a: "至少应包含实时运价引擎、AI 智能报价、订舱与轨迹跟踪、单证智能识别、关务合规支持，以及自动结算与利润分析。CargoNova 在同一平台内提供上述全部六项能力。" },
    { q: "CargoNova 会替换我现有的系统吗？", a: "不会。CargoNova 通过看-算-写（See–Calculate–Write）闭环，既能对接现代 API，也能操作老旧船司门户，无需推倒重来即可落地。" },
    { q: "支持哪些运输方式？", a: "支持海运（整柜与拼箱）、空运与跨境陆运，并内置多币种、多语言运营能力。" },
  ],

  ctaTitle: "用您自己的业务场景验证这些功能",
  ctaBody: "预约演示，我们将把 CargoNova 的 AI Agent 逐一映射到您现有的作业流程。",
};

const contentTh: typeof contentEn = {
  eyebrow: "ฟีเจอร์ของแพลตฟอร์ม",
  title: "ฟีเจอร์ระบบบริหารงานขนส่งด้วย AI\nภายในแพลตฟอร์ม CargoNova",
  summary:
    "CargoNova คือซอฟต์แวร์ฟอร์เวิร์ดเดอร์แบบ AI-Native ระบบบริหารงานขนส่งบนคลาวด์ที่ AI Agent ดูแลเรท ใบเสนอราคา การจอง เอกสาร ศุลกากรและการชำระเงิน ครอบคลุมทางเรือ ทางอากาศ และรถข้ามแดน",
  cta1: "จองเดโม",
  cta2: "CargoNova คืออะไร?",

  painTitle: "ปัญหาที่แพลตฟอร์มนี้แก้ให้",
  pains: [
    "ใบเสนอราคาใช้เวลาหลายชั่วโมง เพราะเรทกระจายอยู่ในอีเมล สเปรดชีตและพอร์ทัลสายเรือ",
    "ข้อมูลชุดเดียวถูกพิมพ์ซ้ำสามสี่รอบระหว่าง HBL ใบแจ้งหนี้และใบขนสินค้า",
    "ระบบ TMS เดิมมีค่าไลเซนส์สูง ใช้เวลาติดตั้งนาน และแก้ไขยาก",
    "เห็นกำไรก็ต่อเมื่อปิดงานแล้ว ไม่ทันแก้ไขระหว่างทาง",
  ],

  featuresTitle: "หกความสามารถหลัก",
  features: [
    { icon: LineChart, title: "การจัดการเรท", body: "รวมเรทสัญญา สปอตและค่าใช้จ่ายเสริมไว้ในเอนจินเดียว อัปเดตอัตโนมัติและเก็บเวอร์ชันตามเส้นทางและสายเรือ" },
    { icon: Sparkles, title: "ใบเสนอราคาด้วย AI", body: "AI Agent อ่านคำขอราคา จับคู่เส้นทางที่ดีที่สุด และออกใบเสนอราคาภายในไม่กี่นาที" },
    { icon: Workflow, title: "จองระวางและติดตาม", body: "ส่งคำสั่งจองไปยังสายเรือและพาร์ทเนอร์ พร้อมติดตามสถานะแบบเรียลไทม์" },
    { icon: FileText, title: "AI อ่านเอกสาร", body: "สกัดและตรวจสอบ HBL, MBL, ใบแจ้งหนี้และแพ็คกิ้งลิสต์อัตโนมัติ ไม่ต้องพิมพ์ซ้ำ" },
    { icon: Globe, title: "ศุลกากรและการปฏิบัติตามกฎ", body: "ร่างและพรีเช็กเอกสารตามกฎของประเทศปลายทาง รองรับหลายประเทศและหลายภาษา" },
    { icon: Wallet, title: "การชำระเงินและกำไร", body: "กระทบยอดต้นทุน รายได้และกระแสเงินสดรายชิปเมนต์ พร้อมแดชบอร์ดกำไรแบบเรียลไทม์" },
  ],

  modesTitle: "รองรับสามรูปแบบการขนส่ง",
  modes: [
    { icon: Ship, title: "ซอฟต์แวร์ขนส่งทางเรือ", body: "FCL และ LCL การจัดการตารางเรือและระวาง ความเสี่ยง D&D และผลงานสายเรือในหน้าจอเดียว" },
    { icon: Plane, title: "ซอฟต์แวร์ขนส่งทางอากาศ", body: "การคำนวณน้ำหนักคิดค่าระวาง การจัดการ AWB วางแผน ULD และจัดการงานเร่งด่วน" },
    { icon: Truck, title: "รถข้ามแดน", body: "เส้นทางจีน–อาเซียน เอกสารผ่านแดนและการปันส่วนต้นทุนหลายช่วง" },
  ],

  compareTitle: "เทียบกับซอฟต์แวร์ฟอร์เวิร์ดเดอร์แบบเดิม",
  compareRows: [
    { label: "สถาปัตยกรรม", legacy: "TMS แบบติดตั้งในองค์กรหรือโฮสต์", nova: "ระบบบริหารงานขนส่งบนคลาวด์แบบ AI-Native" },
    { label: "ราคา", legacy: "ค่าไลเซนส์และโมดูลสูง", nova: "คิดตามการใช้งาน ต้นทุนรวมต่ำกว่ามาก" },
    { label: "ระยะเวลาใช้งานจริง", legacy: "ติดตั้ง 6–18 เดือน", nova: "ภายในไม่กี่สัปดาห์ ไม่ต้องรื้อระบบเดิม" },
    { label: "ระบบอัตโนมัติ", legacy: "กฎและสคริปต์ RPA", nova: "AI Agent ที่ ดู-คำนวณ-เขียนกลับ" },
  ],
  compareCta: "CargoNova vs CargoWise",

  faqTitle: "คำถามที่พบบ่อย",
  faqs: [
    { q: "ระบบบริหารงานขนส่งด้วย AI ควรมีฟีเจอร์อะไรบ้าง?", a: "อย่างน้อยควรมีเอนจินเรทแบบเรียลไทม์ ใบเสนอราคาด้วย AI การจองและติดตามสถานะ การอ่านเอกสาร การรองรับศุลกากร และการกระทบยอดพร้อมรายงานกำไร CargoNova มีครบทั้งหกอย่างในแพลตฟอร์มเดียว" },
    { q: "CargoNova จะแทนที่ระบบเดิมของเราหรือไม่?", a: "ไม่ CargoNova เชื่อมต่อทั้ง API สมัยใหม่และพอร์ทัลระบบเดิมผ่านวงจร ดู-คำนวณ-เขียน จึงเริ่มใช้ได้โดยไม่ต้องรื้อระบบ" },
    { q: "รองรับการขนส่งรูปแบบใดบ้าง?", a: "รองรับทางเรือ (FCL และ LCL) ทางอากาศ และรถข้ามแดน พร้อมการทำงานหลายสกุลเงินและหลายภาษา" },
  ],

  ctaTitle: "ทดลองฟีเจอร์เหล่านี้กับงานจริงของคุณ",
  ctaBody: "จองเดโมแล้วเราจะจับคู่ AI Agent ของ CargoNova เข้ากับกระบวนการทำงานปัจจุบันของคุณ",
};

const Features = () => {
  const { language } = useLanguage();
  const isZh = language === "zh";
  const isTh = language === "th";
  const c = isZh ? contentZh : isTh ? contentTh : contentEn;
  const prefix = language === "en" ? "" : `/${language}`;

  const seoTitle = isZh
    ? "国际货代 AI 系统功能全览 | CargoNova 平台核心能力"
    : isTh
      ? "ฟีเจอร์ระบบบริหารงานขนส่งด้วย AI | CargoNova"
      : "AI Freight Management System Features | CargoNova";

  const seoDesc = isZh
    ? "CargoNova 国际货代 AI 系统功能：运价管理、AI 智能报价、订舱跟踪、单证识别、关务合规与自动结算，覆盖海运、空运与跨境陆运。"
    : isTh
      ? "ฟีเจอร์ของ CargoNova: การจัดการเรท ใบเสนอราคาด้วย AI การจองและติดตาม AI อ่านเอกสาร ศุลกากร และการชำระเงิน ครอบคลุมทางเรือ ทางอากาศและรถข้ามแดน"
      : "CargoNova AI freight management system features: rate management, AI quoting, booking and tracking, document AI, customs compliance and automated settlement for ocean, air and cross-border trucking.";

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={seoTitle}
        description={seoDesc}
        canonical={CANONICAL}
        canonicalZh={CANONICAL_ZH}
        canonicalTh={CANONICAL_TH}
      />
      <Navbar />

      <main className="relative pt-24 pb-20 overflow-hidden bg-gradient-to-tr from-secondary/10 via-background to-accent/10">
        <div className="absolute -top-20 -right-20 w-[36rem] h-[36rem] bg-secondary/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-1/3 -left-32 w-[32rem] h-[32rem] bg-accent/15 rounded-full blur-3xl pointer-events-none" />

        {/* Hero */}
        <section className="relative container mx-auto px-4 max-w-5xl pt-6 md:pt-10 pb-5 md:pb-8">
          <span className="inline-flex items-center gap-1.5 py-1 px-2.5 rounded-full bg-secondary/10 text-secondary text-[11px] font-semibold tracking-wider uppercase mb-4 ring-1 ring-secondary/20">
            <Bot className="w-3 h-3" />
            {c.eyebrow}
          </span>
          <h1
            className={`text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight mb-3 ${
              isZh ? "leading-[1.5159375]" : "leading-[1.25]"
            } bg-gradient-to-r from-foreground via-secondary to-foreground bg-clip-text text-transparent whitespace-pre-line`}
          >
            {c.title}
          </h1>
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-6 max-w-3xl">
            {c.summary}
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              href="https://www.deep-insights.ai/book-demo"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-secondary to-accent px-6 py-3 text-sm font-semibold text-secondary-foreground shadow-lg shadow-secondary/30 transition-transform hover:-translate-y-0.5"
            >
              {c.cta1}
              <ArrowRight className="w-4 h-4" />
            </a>
            <Link
              to={`${prefix}/what-is-cargonova`}
              className="inline-flex items-center gap-2 rounded-full ring-1 ring-border/60 bg-card/70 backdrop-blur px-6 py-3 text-sm font-semibold text-foreground hover:ring-secondary/50 hover:text-secondary transition-colors"
            >
              {c.cta2}
            </Link>
          </div>
        </section>

        {/* Pain points */}
        <section className="relative container mx-auto px-4 max-w-5xl py-8 md:py-12">
          <h2 className="text-xl md:text-2xl font-bold text-foreground mb-6">
            {c.painTitle}
          </h2>
          <div className="rounded-2xl ring-1 ring-border/60 bg-card/70 backdrop-blur p-6 md:p-8">
            <ul className="space-y-4">
              {c.pains.map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-secondary" />
                  <span className="text-sm md:text-base text-muted-foreground leading-relaxed">
                    {p}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Core features */}
        <section className="relative container mx-auto px-4 max-w-5xl py-8 md:py-12">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
            {c.featuresTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {c.features.map((f) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  className="group relative overflow-hidden rounded-2xl bg-card ring-1 ring-border/60 shadow-xs hover:shadow-md transition-all duration-300 p-6"
                >
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-secondary to-accent" />
                  <div className="flex items-center gap-3 mb-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary/10 text-secondary">
                      <Icon className="h-4 w-4" />
                    </span>
                    <h3 className="text-base font-semibold text-foreground leading-snug">
                      {f.title}
                    </h3>
                  </div>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {f.body}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Transport modes */}
        <section className="relative container mx-auto px-4 max-w-5xl py-8 md:py-12">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
            {c.modesTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {c.modes.map((m) => {
              const Icon = m.icon;
              return (
                <div
                  key={m.title}
                  className="rounded-2xl bg-card ring-1 ring-border/60 shadow-xs hover:shadow-md transition-all duration-300 p-6"
                >
                  <span className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="text-base font-semibold text-foreground mb-2 leading-snug">
                    {m.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {m.body}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Comparison */}
        <section className="relative container mx-auto px-4 max-w-5xl py-8 md:py-12">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
            {c.compareTitle}
          </h2>
          <div className="overflow-hidden rounded-2xl ring-1 ring-border/60 bg-card/70 backdrop-blur">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border/60 bg-secondary/5">
                  <th className="p-4 font-semibold text-foreground"> </th>
                  <th className="p-4 font-semibold text-muted-foreground">
                    Legacy TMS
                  </th>
                  <th className="p-4 font-semibold text-secondary">CargoNova</th>
                </tr>
              </thead>
              <tbody>
                {c.compareRows.map((r) => (
                  <tr key={r.label} className="border-b border-border/40 last:border-0">
                    <td className="p-4 font-medium text-foreground">{r.label}</td>
                    <td className="p-4 text-muted-foreground">{r.legacy}</td>
                    <td className="p-4 text-foreground">{r.nova}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-5">
            <Link
              to={`${prefix}/compare/cargonova-vs-cargowise`}
              className="inline-flex items-center gap-2 text-sm font-semibold text-secondary hover:underline"
            >
              {c.compareCta}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>

        {/* FAQ */}
        <section className="relative container mx-auto px-4 max-w-5xl py-8 md:py-12">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
            {c.faqTitle}
          </h2>
          <div className="space-y-4">
            {c.faqs.map((f) => (
              <div
                key={f.q}
                className="rounded-2xl ring-1 ring-border/60 bg-card p-6"
              >
                <h3 className="text-base font-semibold text-foreground mb-2">
                  {f.q}
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {f.a}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="relative container mx-auto px-4 max-w-3xl py-8">
          <div className="rounded-3xl bg-gradient-to-br from-secondary/15 via-card to-accent/10 ring-1 ring-secondary/30 p-6 md:p-8 shadow-[0_20px_60px_-24px_hsl(var(--secondary)/0.35)]">
            <h3 className="text-lg md:text-xl font-bold text-foreground mb-2">
              {c.ctaTitle}
            </h3>
            <p className="text-sm md:text-base text-muted-foreground mb-5">
              {c.ctaBody}
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://www.deep-insights.ai/book-demo"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-secondary to-accent px-6 py-3 text-sm font-semibold text-secondary-foreground shadow-lg shadow-secondary/30 transition-transform hover:-translate-y-0.5"
              >
                {c.cta1}
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link
                to={`${prefix}/pricing`}
                className="inline-flex items-center gap-2 rounded-full ring-1 ring-border/60 bg-card/70 backdrop-blur px-6 py-3 text-sm font-semibold text-foreground hover:ring-secondary/50 hover:text-secondary transition-colors"
              >
                {isZh ? "查看价格" : isTh ? "ดูราคา" : "See pricing"}
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Features;
